import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { useSelector } from "react-redux";
// @mui
import { Card, Stack, Button, Container, Typography } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { Edit } from "@mui/icons-material";
// ----------------------------------------------------------------------

// components
import Iconify from "../components/iconify";
import Scrollbar from "../components/scrollbar";
import Protected from "../protected/Protected";
// sections
import CustomTable from "../components/data-table/CustomDataTable";
import {
  useDeleteCountryMutation,
  useGetCountriesQuery,
} from "../redux/services/country.service";
import CountryForm from "../sections/@dashboard/country/country.form";
import { selectAbility } from "../redux/slices/casl.slice";
import CustomDeleteConfirmation from "../components/dialog/CustomDeleteConfirmation";

export default function CountryPage() {
  const ability = useSelector(selectAbility);

  const { isLoading, isFetching, data: countries } = useGetCountriesQuery();

  const [deleteCountry, { isLoading: isDeleting }] = useDeleteCountryMutation();

  const [openCountryForm, setOpenCountryForm] = useState(false);

  const [edit, setEdit] = useState(false);

  const [openRow, setOpenRow] = useState(null);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const handleCloseForm = () => {
    setOpenCountryForm(false);
    setEdit(false);
  };

  return (
    <Protected>
      <Helmet>
        <title> Country </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Country
          </Typography>
          {ability.can("post", "Country") && (
            <Button
              variant="contained"
              onClick={setOpenCountryForm}
              startIcon={<Iconify icon="eva:plus-fill" />}
            >
              New Country
            </Button>
          )}
          {(ability.can("post", "Country") ||
            ability.can("patch", "Country")) && (
            <CountryForm
              open={openCountryForm}
              onClose={handleCloseForm}
              rows={countries?.data}
              loading={isLoading || isFetching}
              edit={edit}
              openRow={openRow}
            />
          )}
        </Stack>

        <Card>
          <Scrollbar>
            <CustomTable
              canSelect
              tableName="Country"
              useDataQuery={useGetCountriesQuery}
              searchLabel="Search Country..."
              searchCols={["countryCode", "countryName"]}
              actions={
                ability.can("patch", "Country") ||
                ability.can("delete", "Country")
                  ? {
                      field: "actions",
                      headerName: "Action",
                      type: "actions",
                      getActions: ({ row }) =>
                        [
                          ability.can("patch", "Country") ? (
                            <GridActionsCellItem
                              key="edit"
                              icon={<Edit />}
                              label="Edit"
                              showInMenu
                              onClick={() => {
                                setOpenRow(row);
                                setEdit(true);
                                setOpenCountryForm(true);
                              }}
                            />
                          ) : null,
                          ability.can("delete", "Country") ? (
                            <GridActionsCellItem
                              key="delete"
                              sx={{
                                color: "error.main",
                              }}
                              icon={
                                <Iconify
                                  color="error.main"
                                  icon="eva:trash-2-outline"
                                />
                              }
                              label="Delete"
                              showInMenu
                              onClick={() => {
                                setOpenRow(row);
                                setOpenDeleteConfirmation(true);
                              }}
                            />
                          ) : null,
                        ].filter((action) => action !== null),
                    }
                  : null
              }
            />
          </Scrollbar>
        </Card>
      </Container>

      {ability.can("delete", "Country") && (
        <CustomDeleteConfirmation
          open={openDeleteConfirmation}
          onClose={() => setOpenDeleteConfirmation(false)}
          tableName="Country"
          nameKey="countryName"
          nameTitle="name"
          deleteFunc={deleteCountry}
          row={openRow}
          isLoading={isDeleting}
        />
      )}
    </Protected>
  );
}
